import React, { ReactNode, useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import Logo from "./assets/images/logos/ciao-fridays-badge.svg";
import EmailVerificationBanner from "./components/EmailVerificationBanner";
import WhatsAppButton from "./components/WhatsAppButton";
import { useAuth } from "./context/AuthContext";

interface LayoutProps {
  children: ReactNode;
}

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  `font-semibold underline-offset-[6px] decoration-2 hover:underline ${
    isActive ? "underline decoration-gold" : ""
  }`;

const mobileLinkClass = ({ isActive }: { isActive: boolean }) =>
  `block border-b border-cream/10 py-4 font-display text-3xl font-semibold ${
    isActive ? "text-gold" : "text-cream"
  }`;

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, isAdmin, loading, signOut } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close the menu and start at the top whenever the page changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : "";
  }, [isMobileMenuOpen]);

  return (
    <div className="relative flex min-h-screen flex-col">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[70] focus:rounded-full focus:bg-gold focus:px-4 focus:py-2 focus:text-night"
      >
        Skip to content
      </a>

      <header
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-200 ${
          isScrolled || isMobileMenuOpen
            ? "border-b border-cream/10 bg-night/95 backdrop-blur-sm"
            : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-[72px] max-w-6xl items-center justify-between px-5 md:h-20 md:px-8">
          <Link to="/" aria-label="Ciao Fridays, home" className="-ml-1 block">
            <img
              src={Logo}
              alt=""
              width={64}
              height={64}
              className="h-14 w-14 transition-transform duration-300 hover:-rotate-6 md:h-16 md:w-16"
            />
          </Link>

          <nav aria-label="Main" className="hidden items-center gap-7 md:flex">
            <NavLink to="/" end className={navLinkClass}>
              Home
            </NavLink>
            {user && (
              <NavLink to="/events" className={navLinkClass}>
                Events
              </NavLink>
            )}
            {isAdmin && (
              <NavLink to="/admin" className={navLinkClass}>
                Admin
              </NavLink>
            )}
            {!loading &&
              (user ? (
                <button onClick={signOut} className="btn btn-secondary !w-auto !py-2">
                  Sign out
                </button>
              ) : (
                <Link to="/auth" className="btn btn-accent !w-auto !py-2">
                  Sign in
                </Link>
              ))}
          </nav>

          <button
            onClick={() => setIsMobileMenuOpen((open) => !open)}
            className="-mr-2 p-2 md:hidden"
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
          >
            <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.2} strokeLinecap="round">
              {isMobileMenuOpen ? (
                <path d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path d="M4 7.5h16M4 12.5h16M4 17.5h11" />
              )}
            </svg>
          </button>
        </div>
      </header>

      {isMobileMenuOpen && (
        <div id="mobile-menu" className="fixed inset-0 z-40 overflow-y-auto bg-night px-5 pb-10 pt-24 md:hidden">
          <nav aria-label="Main" className="flex flex-col">
            <NavLink to="/" end className={mobileLinkClass}>
              Home
            </NavLink>
            {user && (
              <NavLink to="/events" className={mobileLinkClass}>
                Events
              </NavLink>
            )}
            {isAdmin && (
              <NavLink to="/admin" className={mobileLinkClass}>
                Admin
              </NavLink>
            )}
            <div className="pt-8">
              {user ? (
                <button
                  onClick={() => {
                    signOut();
                    setIsMobileMenuOpen(false);
                  }}
                  className="btn btn-secondary"
                >
                  Sign out
                </button>
              ) : (
                <Link to="/auth" className="btn btn-accent">
                  Sign in
                </Link>
              )}
            </div>
          </nav>
        </div>
      )}

      <main id="main" className="mx-auto w-full max-w-6xl flex-grow px-5 pb-16 pt-24 md:px-8 md:pt-28">
        {children}
      </main>

      <footer className="border-t border-cream/15">
        <div className="mx-auto flex max-w-6xl flex-col gap-6 px-5 py-10 md:flex-row md:items-center md:justify-between md:px-8">
          <div className="flex items-center gap-4">
            <img src={Logo} alt="" width={48} height={48} loading="lazy" className="h-12 w-12" />
            <p className="text-sm text-muted">
              © {new Date().getFullYear()} Ciao Fridays, Luxembourg
              <span className="mt-0.5 block font-handwriting text-lg leading-none text-cream">
                made with love by AVR
              </span>
            </p>
          </div>
          <nav aria-label="Footer" className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm font-semibold">
            <Link to="/terms" className="hover:underline">Terms</Link>
            <Link to="/privacy" className="hover:underline">Privacy</Link>
            <a href="mailto:ciaofridays@gmail.com" className="hover:underline">Contact</a>
            <a
              href="https://www.instagram.com/ciaofridays"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 hover:underline"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 2.16c3.2 0 3.58.01 4.85.07 3.25.15 4.77 1.69 4.92 4.92.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.15 3.23-1.66 4.77-4.92 4.92-1.27.06-1.64.07-4.85.07s-3.58-.01-4.85-.07c-3.26-.15-4.77-1.7-4.92-4.92C2.17 15.58 2.16 15.2 2.16 12s.01-3.58.07-4.85C2.38 3.92 3.9 2.38 7.15 2.23 8.42 2.17 8.8 2.16 12 2.16zM12 0C8.74 0 8.33.01 7.05.07 2.7.27.27 2.69.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.2 4.36 2.62 6.78 6.98 6.98C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c4.35-.2 6.78-2.62 6.98-6.98.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.2-4.35-2.62-6.78-6.98-6.98C15.67.01 15.26 0 12 0zm0 5.84a6.16 6.16 0 100 12.32 6.16 6.16 0 000-12.32zM12 16a4 4 0 110-8 4 4 0 010 8zm6.41-11.85a1.44 1.44 0 100 2.88 1.44 1.44 0 000-2.88z" />
              </svg>
              Instagram
            </a>
          </nav>
        </div>
      </footer>

      {user && <WhatsAppButton />}
      <EmailVerificationBanner />
    </div>
  );
};
export default Layout;
